import type { Route } from '@morales/shared';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty, isIsoDate, todayIso } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { InvoiceDocument } from './InvoicePage';

/**
 * A route on paper (decision 104): the load sheet (stops in load order, the last stop on the truck first, with the
 * totals per item) or every invoice of the route, one per page, for the driver's folder.
 */
export function RoutePrintPage() {
  const { t } = useLocale();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const dateRaw = params.get('date');
  const date = isIsoDate(dateRaw) ? dateRaw : todayIso();
  const what = params.get('what') === 'invoices' ? 'invoices' : 'load';
  const routes = useQuery({ queryKey: qk.routes(date), queryFn: ({ signal }) => api.routes(date, signal) });
  return (
    <QueryView query={routes}>
      {(rs: Route[]) => {
        const r = rs.find((x) => x.id === id);
        if (!r) return <Empty>{t('noRoute')}</Empty>;
        return (
          <div className="stack route-print">
            <PageHead title={`${r.name} · ${what === 'load' ? t('loadSheet') : t('printInvoices')}`} back={`/route?date=${date}`} />
            <div className="contact-actions no-print">
              {what === 'load' ? <PrintButton ready /> : null}
              <Link className="btn btn-ghost" to={`/route/${r.id}/print?date=${date}&what=${what === 'load' ? 'invoices' : 'load'}`}>
                {what === 'load' ? t('printInvoices') : t('loadSheet')}
              </Link>
            </div>
            {what === 'load' ? <LoadSheet route={r} /> : <RouteInvoices route={r} />}
          </div>
        );
      }}
    </QueryView>
  );
}

function LoadSheet({ route: r }: { route: Route }) {
  const { t, intlLocale } = useLocale();
  // what is still to go out: not delivered, not closed as not delivered, not cancelled (decision 182)
  const stops = r.stops.filter((s) => s.deliveredAt === null && s.notDeliveredAt === null && s.orderStatus !== 'cancelled');
  const totals = new Map<string, { name: string; unit: string; qty: number }>();
  for (const s of stops) {
    for (const l of s.lines) {
      const k = `${l.itemName}|${l.unit}`;
      const cur = totals.get(k) ?? { name: l.itemName, unit: l.unit, qty: 0 };
      cur.qty += l.qtyDelivered ?? l.qty;
      totals.set(k, cur);
    }
  }
  return (
    <>
      <p className="muted">
        <strong>{r.name}</strong> · {formatDate(r.date, intlLocale, 'long')} · {t('driver')}: {r.driverName ?? '—'}
        {r.truck ? ` · ${t('truck')} ${r.truck}` : ''}
      </p>
      <p className="note">{t('loadOrderHint')}</p>
      <div className="table-wrap">
        <table className="table" data-testid="load-sheet">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('customer')}</th>
              <th>{t('items')}</th>
              <th className="num">{t('owesNow')}</th>
            </tr>
          </thead>
          <tbody>
            {[...stops].reverse().map((s) => (
              <tr key={s.id}>
                <td>{s.seq}</td>
                <td>
                  {s.customerName}
                  {s.cod ? ` · ${t('codBadge')}` : ''}
                </td>
                <td>{s.lines.map((l) => `${formatQty(l.qtyDelivered ?? l.qty)} ${l.unit} ${l.itemName}`).join(' · ')}</td>
                <td className="num">{s.balance !== null && s.balance > 0 ? formatMoney(s.balance, intlLocale) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>{t('itemTotals')}</h2>
      <div className="table-wrap">
        <table className="table" data-testid="load-totals">
          <tbody>
            {[...totals.values()]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((x) => (
                <tr key={`${x.name}|${x.unit}`}>
                  <td>{x.name}</td>
                  <td className="num">
                    {formatQty(x.qty)} {x.unit}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RouteInvoices({ route: r }: { route: Route }) {
  const { t } = useLocale();
  const ids = r.stops.flatMap((s) => (s.invoiceId ? [s.invoiceId] : []));
  const missing = r.stops.filter((s) => !s.invoiceId);
  const paca = useQuery({ queryKey: qk.pacaSettings, queryFn: ({ signal }) => api.pacaSettings(signal), staleTime: 60 * 60_000, retry: false });
  const company = useQuery({ queryKey: qk.companySettings, queryFn: ({ signal }) => api.companySettings(signal), staleTime: 60 * 60_000, retry: false });
  const invoices = useQueries({ queries: ids.map((invoiceId) => ({ queryKey: qk.invoice(invoiceId), queryFn: ({ signal }: { signal: AbortSignal }) => api.invoice(invoiceId, signal) })) });
  // the driver's folder is complete or not printed: every invoice and the PACA wording loaded first
  const ready = invoices.every((q) => q.isSuccess) && !paca.isPending && !company.isPending;
  const failed = invoices.find((q) => q.isError);
  return (
    <>
      <div className="no-print">
        <PrintButton ready={ready} />
      </div>
      {failed?.error ? <ErrorBox error={failed.error} /> : null}
      {missing.length ? (
        <p className="note no-print" data-testid="not-invoiced">
          {r.name} · {t('notInvoicedYet')}: {missing.map((s) => `${s.seq} ${s.customerName}`).join(', ')}
        </p>
      ) : null}
      {invoices.map((q, i) =>
        q.data ? (
          <div key={ids[i]} className="invoice print-page" data-testid="route-invoice">
            <InvoiceDocument inv={q.data} paca={paca.data} company={company.data} />
          </div>
        ) : null,
      )}
    </>
  );
}

function PrintButton({ ready }: { ready: boolean }) {
  const { t } = useLocale();
  return (
    <button type="button" className="btn btn-primary" disabled={!ready} onClick={() => window.print()}>
      {ready ? t('print') : t('loading')}
    </button>
  );
}
